import { useState, useEffect, useCallback, useRef } from 'react';
import { getDialogueLine } from '@/data/dialogueLines';
import { useAudioManager } from '@/hooks/useAudioManager';
import {
  COUNTRY_NEIGHBORS,
  ANIMATION_DURATIONS,
  AUDIO_PATHS,
  AUDIO_SETTINGS,
  RESOURCE_REQUIREMENTS,
  getCountryPosition,
  getRandomPropagandaSound,
  getRandomBlackmailReactionSound,
  getRandomSuccessCheer,
  getActionName
} from '@/config/gameConstants';
import {
  createAudioInstance,
  createAudioContextWithGain,
  playAudioSafely,
  formatActionNotation,
  isHostileAction,
  getAnimationDuration,
  getDialogueTiming,
  canAffordAction,
  getPlayerCountry,
  getCountryByCode,
  dispatchVictoryEvent,
  createAiActionLogEntry,
  logAction
} from '@/utils/gameUtils';
import { prepareAirStrikeViewport } from '@/utils/mapViewport';

/**
 * Custom hook to manage all game actions and their animations
 * Extracts action handling logic from GameScreen component
 * 
 * @param {Object} game - Current game state
 * @param {Function} setGame - Update game state
 * @param {Function} onAction - Backend action handler
 * @param {Function} showNotification - Show notification function
 * @returns {Object} Action handlers and state
 */
export function useGameActions(game, setGame, onAction, showNotification) {
  // ========== AUDIO MANAGER ==========
  const { 
    playAudio, 
    stopAllAudio,
    stopAllDomAudio: stopDomAudio
  } = useAudioManager();
  
  // ========== STATE ==========
  
  // Action Modal State
  const [lastAction, setLastAction] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Animation States
  const [propagandaAnimation, setPropagandaAnimation] = useState(null);
  const [diplomacyAnimation, setDiplomacyAnimation] = useState(null);
  const [blackmailAnimation, setBlackmailAnimation] = useState(null);
  const [airStrikeAnimation, setAirStrikeAnimation] = useState(null);
  const [deployTroopsAnimation, setDeployTroopsAnimation] = useState(null);
  const [nukeButtonAnimation, setNukeButtonAnimation] = useState(null); // ⭐ Nuke button press video (plays BEFORE missile)
  const [nuclearStrikeAnimation, setNuclearStrikeAnimation] = useState(null);
  const [bomberFlash, setBomberFlash] = useState(null); // New: bomber flash when airstrike available
  
  // Air Strike Lock - prevents ALL interactions during air strike
  const [isAirStrikeLocked, setIsAirStrikeLocked] = useState(false);
  const isAirStrikeLockedRef = useRef(false); // ⭐ Sync ref for immediate reads
  
  // Popup States
  const [deployTroopsConfirmation, setDeployTroopsConfirmation] = useState(null);
  const [enrichmentPopup, setEnrichmentPopup] = useState(null);  // ⭐ Enrichment purchase popup
  const [aaDefensePopup, setAADefensePopup] = useState(null);  // ⭐ AA Defense purchase popup
  
  // Leader Visual States
  const [hiddenLeaderCode, setHiddenLeaderCode] = useState(null);
  const [sweatingLeaders, setSweatingLeaders] = useState([]);
  
  // AI Turn Processing
  const [aiTurnsQueue, setAiTurnsQueue] = useState([]);
  const [isProcessingAiTurns, setIsProcessingAiTurns] = useState(false);
  const [aiActionsLog, setAiActionsLog] = useState([]); // Track AI actions for summary
  
  // Chat Bubble State
  const [activeChatBubbles, setActiveChatBubbles] = useState([]);
  
  // Refs
  const gameRef = useRef(null);
  const prevAirStrikeCooldown = useRef(null);
  const hasPlayedInitialSound = useRef(false); // Track if we've played the initial unlock sound
  const hasPlayedDeployTroopsSound = useRef(false); // Track deploy troops availability sound
  const hasPlayedNuclearLaunchSound = useRef(false); // Track nuclear launch availability sound
  const airStrikeSafetyTimeoutRef = useRef(null); // Track safety timeout for cleanup
  const finalPopulationsRef = useRef({}); // ⭐ Store final populations for incremental animation updates
  
  // Keep gameRef always up-to-date
  useEffect(() => {
    gameRef.current = game;
  }, [game]);
  
  // ========== DERIVED STATE ==========
  
  const playerCountry = game?.countries?.find(c => c.is_player);
  
  // Monitor air strike cooldown and play sound when it becomes available
  useEffect(() => {
    if (!game || !playerCountry) return;
    
    const currentCooldown = playerCountry.air_strike_cooldown || 0;
    const prevCooldown = prevAirStrikeCooldown.current;
    const currentTurn = game.current_turn || 1;
    
    // Check if player can afford air strike (minimum resource requirements)
    const canAffordAirStrike = canAffordAction(playerCountry, RESOURCE_REQUIREMENTS.airStrike);
    
    // Play sound when:
    // 1. Cooldown goes from 1 → 0 (becomes available after cooldown)
    const cooldownEnded = prevCooldown === 1 && currentCooldown === 0 && canAffordAirStrike;
    
    // 2. Turn 2+ and airstrike is available AND affordable for the first time this session
    const isAvailableNow = currentTurn >= 2 && currentCooldown === 0 && canAffordAirStrike;
    const shouldPlayInitialSound = isAvailableNow && !hasPlayedInitialSound.current;
    
    if (cooldownEnded || shouldPlayInitialSound) {
      try {
        // 🐛 iOS FIX: Removed Web Audio API `createMediaElementSource()` which
        // was permanently consuming iOS's single audio stream slot and
        // silently breaking ALL subsequent audio (music, SFX, voiceovers).
        // Use plain HTML5 Audio at max volume — the volume gain was nice
        // but not worth taking out audio on 96% of our users (mobile).
        const availableSound = new Audio(AUDIO_PATHS.airStrikeAvailable);
        availableSound.volume = 1.0;
        availableSound.play().catch(err => console.log('Could not play airstrike available sound:', err));
        console.log('🔊 Air strike is now available!', { turn: currentTurn, cooldown: currentCooldown });
        
        // Show bomber flash animation for 3 seconds
        setBomberFlash({ countryCode: playerCountry.code });
        setTimeout(() => setBomberFlash(null), ANIMATION_DURATIONS.bomberFlashDuration);
        
        // Mark that we've played the initial sound
        if (shouldPlayInitialSound) {
          hasPlayedInitialSound.current = true;
        }
      } catch (err) {
        console.log('Error playing airstrike available sound:', err);
      }
    }
    
    // Update prev cooldown
    prevAirStrikeCooldown.current = currentCooldown;
  }, [game, playerCountry]);
  
  // ========== DEPLOY TROOPS AVAILABILITY SOUND ==========
  // Monitor deploy troops availability and play sound when it becomes available.
  // 🐛 BUG FIX: Previously read `playerCountry.deploy_troops_cooldown` which
  // is NEVER set by the backend. The real cooldown lives at:
  //   - game.deploy_troops_locked[code]   (true once player used 3 deploys)
  //   - game.last_deploy_turn[code]       (3-turn cooldown after each use)
  // Plus the initial cooldown: deploy only unlocks on turn 3 (not turn 2 as
  // the old check assumed). Loading a saved game on turn 2 used to falsely
  // announce "Troop Deployment Available, Sir" even though the action was
  // still on cooldown.
  useEffect(() => {
    if (!game || !playerCountry) return;

    const currentTurn = game.current_turn || 1;
    const code = playerCountry.code;
    const isLocked = !!game.deploy_troops_locked?.[code];
    const lastDeployTurn = game.last_deploy_turn?.[code] || 0;
    const turnsSinceLastDeploy = lastDeployTurn > 0 ? (currentTurn - lastDeployTurn) : 999;
    const initialCooldown = currentTurn < 3; // matches turn_routes.py
    const onCooldown = initialCooldown || isLocked || (lastDeployTurn > 0 && turnsSinceLastDeploy < 3);

    const isDeployTroopsAvailable = !onCooldown;

    if (isDeployTroopsAvailable && !hasPlayedDeployTroopsSound.current) {
      try {
        console.log('🪖 DEPLOY TROOPS NOW AVAILABLE - Playing announcement', {
          currentTurn, isLocked, lastDeployTurn, turnsSinceLastDeploy
        });
        const availableSound = new Audio(AUDIO_PATHS.deployTroopsAvailable);
        availableSound.volume = 1.0;
        availableSound.play()
          .then(() => {
            console.log('✅ Deploy Troops available sound playing');
            hasPlayedDeployTroopsSound.current = true;
          })
          .catch(e => console.log('⚠️ Deploy Troops available sound failed:', e));
      } catch (error) {
        console.log('⚠️ Deploy Troops audio error:', error);
      }
    }
  }, [game, playerCountry]);
  
  // ========== NUCLEAR LAUNCH AVAILABILITY SOUND ==========
  // Monitor nuclear launch availability and play sound when nukes become ready
  useEffect(() => {
    if (!game || !playerCountry) return;
    
    const uraniumEnrichment = playerCountry.resources?.uranium_enrichment || 0;
    const nukesReady = playerCountry.resources?.nukes_ready || 0;
    
    // Check if nuclear launch is available (enrichment >= 100% OR nukes_ready > 0)
    const isNuclearLaunchAvailable = uraniumEnrichment >= 100 || nukesReady > 0;
    
    // Play sound when nuclear launch becomes available for the first time this session
    if (isNuclearLaunchAvailable && !hasPlayedNuclearLaunchSound.current) {
      try {
        console.log('☢️ NUCLEAR LAUNCH NOW AVAILABLE - Playing announcement');
        // 🐛 iOS FIX: Plain HTML5 Audio — Web Audio API was breaking
        // iOS audio for the rest of the session.
        const availableSound = new Audio(AUDIO_PATHS.nuclearLaunchAvailable);
        availableSound.volume = 1.0;
        availableSound.play()
          .then(() => {
            console.log('✅ Nuclear launch available sound playing');
            hasPlayedNuclearLaunchSound.current = true;
          })
          .catch(e => console.log('⚠️ Nuclear launch available sound failed:', e));
          
      } catch (error) {
        console.log('⚠️ Nuclear launch audio error:', error);
      }
    }
  }, [game, playerCountry]);
  
  // ========== CHAT BUBBLE HELPERS ==========
  
  /**
   * Show a chat bubble above a country's leader
   */
  const showChatBubble = useCallback((countryCode, message, duration = 4000) => {
    const bubbleId = `${countryCode}_${Date.now()}`;
    
    setActiveChatBubbles(prev => [
      ...prev,
      { id: bubbleId, countryCode, message, duration }
    ]);
    
    // Auto-remove after duration
    setTimeout(() => {
      setActiveChatBubbles(prev => prev.filter(b => b.id !== bubbleId));
    }, duration);
  }, []);
  
  /**
   * Show dialogue based on action result
   */
  const showActionDialogue = useCallback((countryCode, actionType, success, targetCode = null) => {
    let situation;
    
    if (success) {
      situation = `success_${actionType}`;
    } else {
      situation = 'failure';
    }
    
    const message = getDialogueLine(countryCode, situation);
    if (message) {
      showChatBubble(countryCode, message, 4000);
    }
    
    // Also show "attacked" message for target if it was hostile action
    if (success && targetCode && isHostileAction(actionType)) {
      setTimeout(() => {
        const attackedMessage = getDialogueLine(targetCode, 'attacked');
        if (attackedMessage) {
          showChatBubble(targetCode, attackedMessage, 4000);
        }
      }, ANIMATION_DURATIONS.targetSpeaksAfterAttack);
    }
  }, [showChatBubble]);
  
  // ========== RESULT MODAL ==========
  
  const showResultModal = useCallback((result, actionType, targetCountry) => {
    // Play error sound if action failed
    if (result.success === false) {
      const errorSound = new Audio(AUDIO_PATHS.errorBeep);
      errorSound.volume = AUDIO_SETTINGS.errorVolume;
      errorSound.play().catch(e => console.log('Error sound failed:', e));
    }
    // Play random applause/cheer sound + country propaganda sound if action succeeded
    else if (result.success === true) {
      // Random cheer sound
      const randomCheer = getRandomSuccessCheer();
      const cheerSound = new Audio(randomCheer);
      cheerSound.volume = AUDIO_SETTINGS.cheerVolume;
      cheerSound.play().catch(e => console.log('Cheer sound failed:', e));
      
      // Special case: Blackmail plays TARGET's reaction sounds (not source's propaganda)
      if (actionType === 'blackmail' && targetCountry) {
        const blackmailReactionPath = getRandomBlackmailReactionSound(targetCountry.code);
        
        if (blackmailReactionPath) {
          const blackmailReactionSound = new Audio(blackmailReactionPath);
          blackmailReactionSound.volume = AUDIO_SETTINGS.blackmailReactionVolume;
          blackmailReactionSound.currentTime = AUDIO_SETTINGS.blackmailReactionStartTime;
          blackmailReactionSound.play().catch(e => console.log('Blackmail reaction sound failed:', e));
          console.log(`🕵️ Playing ${targetCountry.code} blackmail REACTION sound`);
        } else {
          console.log(`⚠️ No blackmail reaction sounds for ${targetCountry.code} yet`);
        }
      }
      // For non-blackmail actions: play source country's propaganda sound
      else {
        // ✅ NULL SAFETY: Check game.countries exists
        if (game && game.countries) {
          const sourceCountry = game.countries.find(c => c.is_player);
          if (sourceCountry) {
            const propagandaPath = getRandomPropagandaSound(sourceCountry.code);
            
            if (propagandaPath) {
              playAudio(propagandaPath);
              console.log(`🎵 Playing random ${sourceCountry.code} propaganda sound for successful action`);
            }
          }
        }
      }
    }
    
    // Update game state from result
    if (result.game) {
      setGame(result.game);
    }
    
    // Show result in modal
    setActionResult({
      ...result,
      sourceCountry: playerCountry,
      targetCountry: targetCountry,
      actionType: actionType
    });
    setIsActionModalOpen(true);
  }, [game, playerCountry, setGame]);
  
  // ========== NUKE BUTTON ANIMATION COMPLETION HANDLER ==========
  
  const handleNukeButtonComplete = useCallback(() => {
    if (!nukeButtonAnimation) return;
    
    console.log('🔴 Nuke button animation complete, launching missile...');
    
    const { sourceCountry, targetCountry, result, targetCountryData, isAiAction, success, remainingActions } = nukeButtonAnimation;
    
    // Clear button animation
    setNukeButtonAnimation(null);
    
    // ⭐ STEP 2: Now play the missile/strike animation
    
    // Play launch alert
    const launchAlert = new Audio(AUDIO_PATHS.nuclearLaunchDetected);
    launchAlert.volume = AUDIO_SETTINGS.nuclearLaunchVolume;
    launchAlert.play().catch(e => console.log('Nuclear launch alert failed:', e));
    
    const rawSourcePos = getCountryPosition(sourceCountry);
    const rawTargetPos = getCountryPosition(targetCountry);
    // 📱 Mobile portrait: pre-scroll map + remap to viewport coords so
    // the missile flies from the actual source country, not from the
    // edge of the phone screen.
    const { sourcePos, targetPos } = prepareAirStrikeViewport(rawSourcePos, rawTargetPos);
    
    // Hide target leader and make neighbors sweat
    setHiddenLeaderCode(targetCountry);
    const neighbors = COUNTRY_NEIGHBORS[targetCountry] || [];
    setSweatingLeaders(neighbors);
    console.log(`💦 Neighboring leaders sweating from nuke at ${targetCountry}: ${neighbors.join(', ')}`);
    
    // Start missile animation
    setNuclearStrikeAnimation({
      sourceCode: sourceCountry,
      targetCode: targetCountry,
      sourcePos: sourcePos,
      targetPos: targetPos,
      success: isAiAction ? success : result?.success
    });
    
    // Show dramatic message for AI nukes
    if (isAiAction) {
      setTimeout(() => {
        showNotification(`☢️☢️☢️ NUCLEAR LAUNCH DETECTED ☢️☢️☢️`, 'danger');
      }, 1000);
    }
    
    // After animation, show result or continue AI queue
    const animDuration = isAiAction ? ANIMATION_DURATIONS.aiNuclearStrike : ANIMATION_DURATIONS.nuclearStrike;
    
    setTimeout(() => {
      setNuclearStrikeAnimation(null);
      setHiddenLeaderCode(null);
      setSweatingLeaders([]);
      
      // ⭐ Apply incremental population update for target country (nuke damage)
      if (isAiAction && targetCountry && finalPopulationsRef.current[targetCountry] !== undefined) {
        console.log(`📊 Nuclear strike complete - Updating ${targetCountry} population to ${finalPopulationsRef.current[targetCountry]}M`);
        setGame(prev => ({
          ...prev,
          countries: prev.countries.map(c => 
            c.code === targetCountry 
              ? { ...c, resources: { ...c.resources, population: finalPopulationsRef.current[c.code] }}
              : c
          )
        }));
      }
      
      // Player nuke: show result modal
      if (!isAiAction && result) {
        showResultModal(result, 'nuclear_strike', targetCountryData);
        setIsProcessing(false);
        
        if (result.turn_changed) {
          console.log('🎬 Turn changed detected in result, calling handleEndTurn');
          // Note: handleEndTurnFn is not available here, but turn_changed should be handled elsewhere
        }
      }
      
      // AI nuke: continue with remaining actions
      if (isAiAction && remainingActions) {
        if (remainingActions.length > 0) {
          setTimeout(() => {
            setAiTurnsQueue(remainingActions);
          }, 2000); // Gap between actions
        } else {
          setAiTurnsQueue([]);
          console.log('✅ All AI turns complete');
          setIsProcessingAiTurns(false);
          
          // Refresh game state
          console.log('🔄 Refreshing game state to update resource counters...');
          fetchLatestGameState();
        }
      }
      
    }, animDuration);
    
  }, [nukeButtonAnimation, setHiddenLeaderCode, setSweatingLeaders, setNuclearStrikeAnimation, showResultModal, setIsProcessing, showNotification, setAiTurnsQueue, setIsProcessingAiTurns]);
  
  // ========== AUTO END TURN ==========
  
  const handleAutoEndTurn = useCallback(async (handleEndTurnFn) => {
    try {
      // FIX: Use latest game state directly instead of gameRef to avoid race conditions
      // The gameRef.current might be stale when React state updates haven't propagated yet
      const currentGame = game;
      if (!currentGame) {
        console.log('[AUTO END TURN] No current game state');
        return;
      }
      
      const currentPlayerCountry = currentGame.countries.find(c => c.is_player);
      if (!currentPlayerCountry) {
        console.log('[AUTO END TURN] No player country found');
        return;
      }
      
      console.log(`[AUTO END TURN] Player: ${currentPlayerCountry.name}, actions: ${currentPlayerCountry.actions_remaining}`);
      
      // Only end turn if player has 0 actions left
      if (currentPlayerCountry.actions_remaining <= 0) {
        console.log('[AUTO END TURN] ✅ Triggering auto-advance (actions exhausted)');
        // Small delay to ensure all UI updates complete
        await new Promise(resolve => setTimeout(resolve, ANIMATION_DURATIONS.autoEndTurnDelay));
        await handleEndTurnFn();
      } else {
        console.log(`[AUTO END TURN] ⏸️ Player still has ${currentPlayerCountry.actions_remaining} actions, not advancing`);
      }
    } catch (error) {
      console.error('[AUTO END TURN] ❌ Failed:', error);
      // Don't throw - let the game continue even if auto-advance fails
    }
  }, [game]);
  
  // ========== PLAYER ACTION HANDLER ==========
  
  const handleAction = useCallback(async (actionType, targetCode, handleEndTurnFn) => {
    // ✅ NULL SAFETY: Prevent all actions if game or game.countries is null
    if (!game || !game.countries) {
      console.warn('⚠️ Cannot perform action - game state is null');
      return;
    }
    
    // ⏭️ SKIP TURN - immediately end turn without any action
    if (actionType === 'skip_turn') {
      console.log('⏭️ Player chose to SKIP ROUND');
      setIsProcessing(true);
      
      // Show brief notification
      setTimeout(() => {
        setIsProcessing(false);
        // Call end turn immediately
        handleEndTurnFn();
      }, 500);
      
      return;
    }
    
    // Block ALL actions during air strike animation
    // ⭐ Use REF for synchronous check (avoids stale closure issues with rapid clicks)
    if (isAirStrikeLockedRef.current) {
      console.log('🔒 Action blocked - Air strike in progress');
      return;
    }
    
    // Intercept Deploy Troops to show confirmation popup
    if (actionType === 'deploy_troops') {
      const sourceCountry = game.countries.find(c => c.is_player);
      const targetCountry = game.countries.find(c => c.code === targetCode);
      
      // Small delay to ensure clean UI transition
      setTimeout(() => {
        setDeployTroopsConfirmation({
          sourceCountry,
          targetCountry
        });
      }, 100);
      
      return; // Don't execute action yet, wait for confirmation
    }
    
    setIsProcessing(true);
    
    try {
      const result = await onAction(actionType, targetCode);
      
      if (!result) {
        setIsProcessing(false);
        return;
      }
      
      const willTriggerTurnChange = result.turn_changed;
      
      // ===== PROPAGANDA ACTION =====
      if (actionType === 'propaganda') {
        const targetCountry = game.countries.find(c => c.code === targetCode);
        
        setPropagandaAnimation({
          source: playerCountry.code,
          target: targetCode,
          success: result.success,
          phase: 'propaganda'
        });
        
        setTimeout(() => {
          if (result.success) {
            // Play cheer + propaganda sounds
            const randomCheer = getRandomSuccessCheer();
            const cheerSound = new Audio(randomCheer);
            cheerSound.volume = AUDIO_SETTINGS.cheerVolume;
            cheerSound.play().catch(e => console.log('Cheer sound failed:', e));
            
            // Play country-specific propaganda sound
            const propagandaPath = getRandomPropagandaSound(playerCountry.code);
            if (propagandaPath) {
              playAudio(propagandaPath);
            }
            
            setPropagandaAnimation(prev => ({ ...prev, phase: 'celebration' }));
            
            setTimeout(async () => {
              setPropagandaAnimation(null);
              showResultModal(result, actionType, targetCountry);
              setIsProcessing(false);
              
              // Check the RESULT, not current game state (which might be stale)
              if (result.turn_changed) {
                console.log('🎬 Turn changed detected in result, calling handleEndTurn');
                setTimeout(() => handleEndTurnFn(), 1000);
              }
            }, ANIMATION_DURATIONS.propagandaCelebration);
          } else {
            setPropagandaAnimation(null);
            showResultModal(result, actionType, targetCountry);
            setIsProcessing(false);
            
            // Check the RESULT, not current game state
            if (result.turn_changed) {
              console.log('🎬 Turn changed detected in result, calling handleEndTurn');
              setTimeout(() => handleEndTurnFn(), 1000);
            }
          }
        }, ANIMATION_DURATIONS.propaganda);
        
      // ===== DIPLOMACY ACTION =====
      } else if (actionType === 'diplomacy') {
        const targetCountry = game.countries.find(c => c.code === targetCode);
        
        setDiplomacyAnimation({
          sourceCode: playerCountry.code,
          targetCode: targetCode
        });
        
        setTimeout(() => {
          setDiplomacyAnimation(null);
          showResultModal(result, actionType, targetCountry);
          showActionDialogue(playerCountry.code, actionType, result.success, targetCode);
          setIsProcessing(false);
          
          if (result.turn_changed) {
            console.log('🎬 Turn changed detected in result, calling handleEndTurn'); setTimeout(() => handleEndTurnFn(), 1000);
          }
        }, ANIMATION_DURATIONS.diplomacy);
        
      // ===== BLACKMAIL ACTION =====
      } else if (actionType === 'blackmail') {
        const targetCountry = game.countries.find(c => c.code === targetCode);
        setBlackmailAnimation({ targetCode: targetCode });
        
        setTimeout(() => {
          setBlackmailAnimation(null);
          showResultModal(result, actionType, targetCountry);
          showActionDialogue(playerCountry.code, actionType, result.success, targetCode);
          setIsProcessing(false);
          
          if (result.turn_changed) {
            console.log('🎬 Turn changed detected in result, calling handleEndTurn'); setTimeout(() => handleEndTurnFn(), 1000);
          }
        }, ANIMATION_DURATIONS.blackmail);
        
      // ===== AIR STRIKE ACTION =====
      } else if (actionType === 'air_strike') {
        console.log('✈️ AIR STRIKE - Starting, will LOCK map');
        
        // LOCK EVERYTHING - Map is now READ-ONLY
        setIsAirStrikeLocked(true);
        isAirStrikeLockedRef.current = true; // ⭐ Sync ref for immediate reads
        console.log('✈️ AIR STRIKE - Map LOCKED (state + ref)');
        
        // ⭐ SAFETY: Ensure lock clears even if something goes wrong
        // Maximum air strike duration + buffer
        const maxDuration = ANIMATION_DURATIONS.airStrikeSuccess + ANIMATION_DURATIONS.airStrikeDelayAfterAnimation + 5000;
        
        // Clear any existing safety timeout
        if (airStrikeSafetyTimeoutRef.current) {
          clearTimeout(airStrikeSafetyTimeoutRef.current);
        }
        
        // Set new safety timeout
        airStrikeSafetyTimeoutRef.current = setTimeout(() => {
          console.log('⚠️ AIR STRIKE SAFETY TIMEOUT - Force unlocking map after', maxDuration, 'ms');
          setIsAirStrikeLocked(false);
          isAirStrikeLockedRef.current = false; // ⭐ Sync ref for immediate reads
          airStrikeSafetyTimeoutRef.current = null;
        }, maxDuration);
        
        console.log('✈️ AIR STRIKE - Safety timeout set for', maxDuration, 'ms');
        
        // Clear any existing modals/results/notifications immediately
        setActionResult(null);
        setIsActionModalOpen(false);
        
        const targetCountry = game.countries.find(c => c.code === targetCode);
        const rawSourcePos = getCountryPosition(playerCountry.code);
        const rawTargetPos = getCountryPosition(targetCode);
        // 📱 On mobile portrait, scroll the map so source+target are
        // visible and remap their positions from map-% to viewport-%
        // so the bomber doesn't fly from the screen edge.
        const { sourcePos, targetPos } = prepareAirStrikeViewport(rawSourcePos, rawTargetPos);
        
        console.log('✈️ AIR STRIKE - Map LOCKED, starting animation:', { 
          source: playerCountry.name, 
          target: targetCountry.name,
          sourcePos,
          targetPos,
          success: result.success,
          targetAALevel: targetCountry.air_defense_level
        });
        
        setAirStrikeAnimation({
          sourceCountry: playerCountry.code,
          targetCountry: targetCode,
          sourcePos,
          targetPos,
          success: result.success,  // Pass success/failure from backend
          targetAADefenseLevel: targetCountry.air_defense_level || 0,  // ⭐ NEW: Pass AA tier to animation
          onAnimationComplete: () => {
            // Called when bomber animation naturally completes its journey
            console.log('✈️ AIR STRIKE - Clearing animation (bomber returned home)');
            setAirStrikeAnimation(null);
            
            // Wait 2 seconds before showing result AND unlocking map
            setTimeout(() => {
              console.log('✈️ AIR STRIKE - UNLOCKING map, showing result popup AND notification');
              setIsAirStrikeLocked(false); // UNLOCK map
              isAirStrikeLockedRef.current = false; // ⭐ Sync ref for immediate reads
              
              // Clear safety timeout since we're unlocking normally
              if (airStrikeSafetyTimeoutRef.current) {
                clearTimeout(airStrikeSafetyTimeoutRef.current);
                airStrikeSafetyTimeoutRef.current = null;
                console.log('✈️ AIR STRIKE - Safety timeout cleared (normal unlock)');
              }
              
              // Show notification NOW (after animation + delay)
              showNotification(
                result.message, 
                result.success ? 'success' : 'danger'
              );
              
              // ☠️ CHECK FOR LEADER ASSASSINATION
              if (result.leader_killed) {
                console.log('💀 LEADER ASSASSINATION DETECTED!', result.leader_killed);
                setTimeout(() => {
                  // Dispatch custom event for victory modal
                  dispatchVictoryEvent(
                    result.leader_killed,
                    result.leader_killed.stats
                  );
                  
                  // Play sad/ominous music
                  try {
                    const defeatSound = createAudioInstance(AUDIO_PATHS.defeat, {
                      volume: AUDIO_SETTINGS.defeatVolume
                    });
                    playAudioSafely(defeatSound, 'Defeat music');
                  } catch (err) {
                    console.log('Defeat music error:', err);
                  }
                }, ANIMATION_DURATIONS.leaderAssassinationDelay);
              }
              
              showResultModal(result, actionType, targetCountry);
              showActionDialogue(playerCountry.code, actionType, result.success, targetCode);
              setIsProcessing(false);
              
              if (result.turn_changed) {
                console.log('🎬 Turn changed detected in result, calling handleEndTurn'); setTimeout(() => handleEndTurnFn(), 1000);
              }
            }, ANIMATION_DURATIONS.airStrikeDelayAfterAnimation);
          }
        });
        
        return; // Exit early, don't show modal immediately
        
      // ===== DEPLOY TROOPS ACTION =====
      } else if (actionType === 'deploy_troops') {
        const targetCountry = game.countries.find(c => c.code === targetCode);
        console.log('🪖 DEPLOY TROOPS - Setting animation state:', { 
          source: playerCountry.name, 
          target: targetCountry.name,
          actions_remaining: result.actions_remaining,
          turn_changed: result.turn_changed
        });
        setDeployTroopsAnimation({
          source: playerCountry.name,
          target: targetCountry.name
        });
        
        setTimeout(() => {
          console.log('🪖 DEPLOY TROOPS - Animation complete, showing result');
          setDeployTroopsAnimation(null);
          showResultModal(result, actionType, targetCountry);
          showActionDialogue(playerCountry.code, actionType, result.success, targetCode);
          setIsProcessing(false);
          
          if (result.turn_changed) {
            console.log('🎬 Turn changed detected in DEPLOY TROOPS result:', { 
              turn_changed: result.turn_changed, 
              handleEndTurnFn: typeof handleEndTurnFn,
              actions_remaining: result.actions_remaining 
            });
            if (typeof handleEndTurnFn === 'function') {
              console.log('✅ Calling handleEndTurnFn() in 1000ms...');
              setTimeout(() => {
                console.log('⏰ handleEndTurnFn() executing NOW');
                handleEndTurnFn();
              }, 1000);
            } else {
              console.error('❌ handleEndTurnFn is not a function!', handleEndTurnFn);
            }
          } else {
            console.log('ℹ️ Turn has NOT changed - actions_remaining:', result.actions_remaining);
          }
        }, ANIMATION_DURATIONS.deployTroops);
        
      // ===== NUCLEAR STRIKE ACTION =====
      } else if (actionType === 'nuclear_strike') {
        const targetCountry = game.countries.find(c => c.code === targetCode);
        
        console.log('🔴 PLAYER NUKE: Showing button press animation first...');
        
        // ⭐ STEP 1: Show nuke button press animation FIRST
        setNukeButtonAnimation({
          sourceCountry: playerCountry.code,
          targetCountry: targetCode,
          result: result,  // Store result for later
          targetCountryData: targetCountry
        });
        
        // ⭐ The missile animation will be triggered after button video completes
        // (handled by the nukeButtonAnimation completion handler below)
        
      // ===== OTHER ACTIONS =====
      } else {
        const targetCountry = game.countries.find(c => c.code === targetCode);
        
        // ⭐ BUILD AA DEFENSE: Show popup + play cash register
        if (actionType === 'build_aa_defense') {
          // Play cash register sound
          const cashSound = new Audio(AUDIO_PATHS.cashRegister);
          cashSound.volume = 0.5;
          cashSound.play().catch(e => console.log('Cash register sound failed:', e));
          
          // Show AA purchase popup
          setAADefensePopup({
            countryCode: playerCountry.code,
            countryName: playerCountry.name,
            tier: result.new_aa_level || 1,
            cost: result.cost || 0,
            successRate: result.new_success_rate || 0
          });
          
          // Auto-dismiss after 5 seconds
          setTimeout(() => {
            setAADefensePopup(null);
          }, 5000);
          
          console.log('🛡️ Showing AA Defense purchase popup:', {
            tier: result.new_aa_level,
            cost: result.cost,
            rate: result.new_success_rate
          });
          
          // Don't show result modal for AA purchase (popup is enough)
          setIsProcessing(false);
          
          // Refresh game state after popup completes
          setTimeout(() => {
            fetchLatestGameState();
          }, 5500);
          
          if (result.turn_changed) {
            console.log('🎬 Turn changed detected in result, calling handleEndTurn'); 
            setTimeout(() => handleEndTurnFn(), 6000);
          }
          
          return; // Exit early - AA purchase handled
        }
        
        // ⭐ TRAIN MILITARY: Play special audio + cash register (layered)
        if (actionType === 'train_military') {
          // Play train military sound
          const trainSound = new Audio(AUDIO_PATHS.trainMilitary);
          trainSound.volume = 0.7;
          trainSound.play().catch(e => console.log('Train military sound failed:', e));
          
          // Layer cash register sound (economic transaction)
          const cashSound = new Audio(AUDIO_PATHS.cashRegister);
          cashSound.volume = 0.4;  // Lower volume so it doesn't overpower
          cashSound.play().catch(e => console.log('Cash register sound failed:', e));
          
          console.log('🎖️ Playing train military audio + cash register');
        }
        
        if (result.success) {
          setPropagandaAnimation({
            source: playerCountry.code,
            target: targetCode,
            success: true,
            phase: 'celebration'
          });
          
          setTimeout(async () => {
            setPropagandaAnimation(null);
            showResultModal(result, actionType, targetCountry);
            setIsProcessing(false);
            
            if (result.turn_changed) {
              console.log('🎬 Turn changed detected in result, calling handleEndTurn'); setTimeout(() => handleEndTurnFn(), 1000);
            }
          }, 5000);
        } else {
          showResultModal(result, actionType, targetCountry);
          setIsProcessing(false);
          
          if (result.turn_changed) {
            console.log('🎬 Turn changed detected in result, calling handleEndTurn'); setTimeout(() => handleEndTurnFn(), 1000);
          }
        }
      }
    } catch (error) {
      console.error('Action error:', error);
      setIsProcessing(false);
    }
  }, [game, playerCountry, onAction, showResultModal, showActionDialogue, handleAutoEndTurn, setGame]);
  
  // ========== AI TURN PROCESSING ==========
  
  useEffect(() => {
    console.log('🔄 AI Queue effect triggered:', {
      queueLength: aiTurnsQueue.length,
      isProcessing: isProcessingAiTurns,
      queue: aiTurnsQueue
    });
    
    if (aiTurnsQueue.length === 0 || !isProcessingAiTurns) {
      return;
    }

    const processNextAiAction = async () => {
      const [currentAction, ...remainingActions] = aiTurnsQueue;
      logAction('🤖', `AI Action: ${currentAction.country} → ${currentAction.action} → ${currentAction.target}`);
      
      // Log AI action for summary modal
      setAiActionsLog(prev => [...prev, createAiActionLogEntry(currentAction)]);

      const actionNotation = formatActionNotation(
        currentAction.action,
        currentAction.country_code,
        currentAction.target_code
      );
      
      // Play feedback sound
      const playFeedbackSound = (success) => {
        const soundPath = success ? AUDIO_PATHS.cheer : AUDIO_PATHS.boo;
        const feedbackSound = createAudioInstance(soundPath, {
          volume: AUDIO_SETTINGS.feedbackVolume,
          startTime: AUDIO_SETTINGS.feedbackStartTime
        });
        playAudioSafely(feedbackSound, 'Feedback sound');
      };
      
      // Show notification
      if (currentAction.success) {
        playFeedbackSound(true);
        showNotification(`✅ ${actionNotation} - SUCCESS`, 'success');
      } else {
        playFeedbackSound(false);
        showNotification(`❌ ${actionNotation} - FAILED`, 'danger');
      }

      // Stop all previous audio
      stopDomAudio();

      // Set last action for leader emotion changes
      setLastAction({
        result: {
          source: currentAction.country_code,
          target: currentAction.target_code,
          success: currentAction.success,
          type: currentAction.action
        }
      });

      // Calculate animation duration based on action type
      let animationDuration = getAnimationDuration(currentAction.action, true, currentAction.success);

      // Trigger animations
      if (currentAction.action === 'propaganda') {
        setPropagandaAnimation({
          source: currentAction.country_code,
          target: currentAction.target_code,
          success: currentAction.success,
          phase: 'propaganda'
        });
        
        setTimeout(() => {
          showActionDialogue(currentAction.country_code, 'propaganda', currentAction.success, currentAction.target_code);
        }, getDialogueTiming('propaganda'));
        
        setTimeout(() => {
          setPropagandaAnimation(null);
        }, animationDuration);
        
      } else if (currentAction.action === 'diplomacy') {
        setDiplomacyAnimation({
          sourceCode: currentAction.country_code,
          targetCode: currentAction.target_code
        });
        
        setTimeout(() => {
          showActionDialogue(currentAction.country_code, 'diplomacy', currentAction.success, currentAction.target_code);
        }, getDialogueTiming('diplomacy'));
        
        setTimeout(() => {
          setDiplomacyAnimation(null);
        }, animationDuration);
        
      } else if (currentAction.action === 'blackmail') {
        setBlackmailAnimation({ targetCode: currentAction.target_code });
        
        setTimeout(() => {
          showActionDialogue(currentAction.country_code, 'blackmail', currentAction.success, currentAction.target_code);
        }, getDialogueTiming('blackmail'));
        
        setTimeout(() => {
          setBlackmailAnimation(null);
        }, animationDuration);
        
      } else if (currentAction.action === 'deploy_troops') {
        setDeployTroopsAnimation({
          source: currentAction.country_code,
          target: currentAction.target_code
        });
        
        setTimeout(() => {
          showActionDialogue(currentAction.country_code, 'deploy_troops', currentAction.success, currentAction.target_code);
        }, getDialogueTiming('deploy_troops'));
        
        setTimeout(() => {
          setDeployTroopsAnimation(null);
        }, animationDuration);
      } else if (currentAction.action === 'air_strike') {
        // ✈️ AIR STRIKE ANIMATION
        logAction('✈️', `AI AIR STRIKE: ${currentAction.country} → ${currentAction.target}`);
        
        const rawSourcePos = getCountryPosition(currentAction.country_code);
        const rawTargetPos = getCountryPosition(currentAction.target_code);
        // 📱 Mobile portrait: scroll map and remap to viewport coords
        const { sourcePos, targetPos } = prepareAirStrikeViewport(rawSourcePos, rawTargetPos);
        
        // ⭐ Get target country data for AA defense level
        const targetCountryData = game.countries.find(c => c.code === currentAction.target_code);
        
        setAirStrikeAnimation({
          sourceCountry: currentAction.country_code,
          targetCountry: currentAction.target_code,
          sourcePos: sourcePos,
          targetPos: targetPos,
          success: currentAction.success,
          targetAADefenseLevel: targetCountryData?.air_defense_level || 0,  // ⭐ NEW: Pass AA tier
          onAnimationComplete: () => {
            // Called when AI bomber animation naturally completes
            setAirStrikeAnimation(null);
            
            // ⭐ Apply incremental population update for target country
            if (currentAction.success && finalPopulationsRef.current[currentAction.target_code] !== undefined) {
              console.log(`📊 Air strike complete - Updating ${currentAction.target_code} population to ${finalPopulationsRef.current[currentAction.target_code]}M`);
              setGame(prev => ({
                ...prev,
                countries: prev.countries.map(c => 
                  c.code === currentAction.target_code 
                    ? { ...c, resources: { ...c.resources, population: finalPopulationsRef.current[c.code] }}
                    : c
                )
              }));
            }
          }
        });
        
        // Show action dialogue near the end
        setTimeout(() => {
          showActionDialogue(currentAction.country_code, 'air_strike', currentAction.success, currentAction.target_code);
        }, getDialogueTiming('air_strike', currentAction.success));
      } else if (currentAction.action === 'nuclear_strike') {
        // ☢️☢️☢️ NUCLEAR STRIKE - THE MAIN EVENT! ☢️☢️☢️
        console.log('☢️☢️☢️ AI NUCLEAR STRIKE - STARTING BUTTON PRESS ANIMATION!');
        console.log(`☢️ ${currentAction.country} launching nuke at ${currentAction.target}`);
        
        // ⭐ STEP 1: Show nuke button animation FIRST (for AI too!)
        setNukeButtonAnimation({
          sourceCountry: currentAction.country_code,
          targetCountry: currentAction.target_code,
          success: currentAction.success,
          isAiAction: true,  // Flag to identify this is from AI
          remainingActions: remainingActions  // Pass queue to continue after animation
        });
        
        // ⭐ CRITICAL: Return early - handleNukeButtonComplete will manage queue continuation
        return;
      } else if (currentAction.action === 'radiation_damage') {
        // ☢️ RADIATION DAMAGE - Gradual population loss over 3 turns
        console.log(`☢️ RADIATION DAMAGE: ${currentAction.country} suffering fallout`);
        
        // Show radiation effect notification
        showNotification(currentAction.message || `☢️ ${currentAction.country} suffers radiation damage`, 'danger');
        
        animationDuration = ANIMATION_DURATIONS.aiRadiationDamage;
      } else if (currentAction.action === 'buy_enrichment') {
        // 💰☢️ ENRICHMENT PURCHASE - AI bought uranium enrichment
        console.log(`💰 ENRICHMENT PURCHASE: ${currentAction.country} bought +${currentAction.enrichment_gained}% for $${currentAction.cost}M`);
        
        // Show enrichment popup for AI purchase
        setEnrichmentPopup({
          countryCode: currentAction.country_code,
          countryName: currentAction.country,
          enrichmentGained: currentAction.enrichment_gained,
          cost: currentAction.cost,
          newEnrichment: currentAction.new_enrichment
        });
        
        // Popup auto-dismisses after 3 seconds
        animationDuration = 3000;
        
        setTimeout(() => {
          setEnrichmentPopup(null);
        }, animationDuration);
      } else if (currentAction.action === 'build_aa_defense') {
        // 🛡️ AA DEFENSE PURCHASE - AI bought/upgraded AA defenses
        console.log(`🛡️ AA DEFENSE PURCHASE: ${currentAction.country} upgraded to Tier ${currentAction.new_aa_level} for $${currentAction.cost}M`);
        
        // Show AA defense popup for AI purchase
        setAADefensePopup({
          countryCode: currentAction.country_code,
          countryName: currentAction.country,
          tier: currentAction.new_aa_level,
          cost: currentAction.cost,
          successRate: currentAction.new_success_rate || 0
        });
        
        // Popup auto-dismisses after 5 seconds
        animationDuration = 5000;
        
        setTimeout(() => {
          setAADefensePopup(null);
        }, animationDuration);
      }

      // SLOWED DOWN: Longer gap between actions (2 seconds instead of 500ms)
      setTimeout(() => {
        if (remainingActions.length > 0) {
          setAiTurnsQueue(remainingActions);
        } else {
          setAiTurnsQueue([]);
          console.log('✅ All AI turns complete');
          setIsProcessingAiTurns(false);
          
          // ⭐ Apply any remaining population updates that weren't handled by animations
          // (e.g., radiation damage, resource changes from non-combat actions)
          console.log('📊 Applying any remaining final population values...');
          setGame(prev => ({
            ...prev,
            countries: prev.countries.map(c => ({
              ...c,
              resources: {
                ...c.resources,
                population: finalPopulationsRef.current[c.code] ?? c.resources.population
              }
            }))
          }));
          
          // AI actions log will be available for summary modal in GameScreen
          // The log is already being populated during each AI action
        }
      }, animationDuration + ANIMATION_DURATIONS.gapBetweenAiActions);
    };

    processNextAiAction();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTurnsQueue, isProcessingAiTurns]);
  
  // ========== FETCH LATEST GAME STATE ==========
  // Called to refresh non-population game state (resources, cooldowns, etc)
  // NOTE: Does NOT update populations during AI animations (handled incrementally)
  const fetchLatestGameState = useCallback(async () => {
    if (!game) return;
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/games/${game.id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch latest game state:', response.statusText);
        return;
      }
      
      const data = await response.json();
      
      // ⭐ PRESERVE CURRENT POPULATIONS - don't override incremental updates
      const preservedGame = {
        ...data,
        countries: data.countries.map(country => {
          const currentCountry = game.countries.find(c => c.code === country.code);
          return {
            ...country,
            resources: {
              ...country.resources,
              population: currentCountry?.resources?.population ?? country.resources.population
            }
          };
        })
      };
      
      console.log('✅ Game state refreshed (populations preserved for animations)');
      setGame(preservedGame);
    } catch (error) {
      console.error('Error refreshing game state:', error);
    }
  }, [game, setGame]);
  
  // ========== END TURN HANDLER ==========
  
  const handleEndTurn = useCallback(async () => {
    if (!game) return;
    
    setIsProcessing(true);
    setIsProcessingAiTurns(true);
    showNotification('Processing AI turns...', 'info');
    
    try {
      console.log('🎬 END TURN CALLED - Starting AI turn processing...');
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/games/end-turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: game.id })
      });
      
      const data = await response.json();
      console.log('📡 End turn response received:', data);
      
      // ⭐ INCREMENTAL POPULATION UPDATE FIX:
      // Store final populations in ref, but don't apply them yet
      // They'll be applied incrementally as each animation completes
      const finalGame = { ...data.game };
      finalPopulationsRef.current = {};
      
      // Store final populations and preserve current populations in game state
      finalGame.countries = finalGame.countries.map(country => {
        const currentCountry = game.countries.find(c => c.code === country.code);
        const currentPop = currentCountry?.resources?.population || country.resources.population;
        
        // Store final population for later incremental updates
        finalPopulationsRef.current[country.code] = country.resources.population;
        
        // Preserve current population (don't update yet - animations will do it)
        return {
          ...country,
          resources: {
            ...country.resources,
            population: currentPop  // Keep current value, don't jump to final
          }
        };
      });
      
      console.log('📊 Stored final populations for incremental updates:', finalPopulationsRef.current);
      
      // Update game state with preserved populations
      setGame(finalGame);
      
      // Return nuke ready info for parent component to handle
      const nukeReadyData = data.nuke_ready && data.nuke_ready.length > 0 ? data.nuke_ready[0] : null;
      
      // Queue AI actions
      console.log('📦 End turn response:', {
        has_ai_actions: !!data.ai_actions,
        ai_actions_length: data.ai_actions?.length || 0,
        ai_actions: data.ai_actions
      });
      
      if (data.ai_actions && data.ai_actions.length > 0) {
        console.log('🤖 Setting AI turns queue with', data.ai_actions.length, 'actions');
        
        // Check if any AI performed an air strike and play siren sound
        const hasAirStrike = data.ai_actions.some(action => action.action === 'air_strike');
        if (hasAirStrike) {
          try {
            const siren = new Audio(AUDIO_PATHS.aiAirStrikeSiren);
            siren.volume = AUDIO_SETTINGS.aiAirStrikeSirenVolume;
            siren.play().catch(err => console.log('Could not play AI air strike siren:', err));
          } catch (err) {
            console.log('Error creating AI air strike siren audio:', err);
          }
        }
        
        setAiTurnsQueue(data.ai_actions);
      } else {
        console.log('⚠️ No AI actions returned from backend');
        setIsProcessingAiTurns(false);
        showNotification(`Turn ${data.current_turn} - Your turn! (3 actions)`, 'success');
      }
      
      return { nukeReady: nukeReadyData };
    } catch (error) {
      console.error('End turn failed:', error);
      setIsProcessingAiTurns(false);
      showNotification('Failed to end turn', 'danger');
      return { error };
    } finally {
      setIsProcessing(false);
    }
  }, [game, setGame, showNotification]);
  
  // ========== SKIP AI TURNS ==========
  
  const skipAiTurns = useCallback(() => {
    console.log('⏭️ Skipping remaining AI turns');
    
    // Clear AI queue and stop processing
    setAiTurnsQueue([]);
    setIsProcessingAiTurns(false);
    
    // Clear all animations
    setPropagandaAnimation(null);
    setDiplomacyAnimation(null);
    setBlackmailAnimation(null);
    setDeployTroopsAnimation(null);
    setNuclearStrikeAnimation(null);
    setAirStrikeAnimation(null);  // ⭐ Also clear air strike animation
    
    // ⭐ UNLOCK AIR STRIKE: Clear the lock in case it was stuck
    setIsAirStrikeLocked(false);
    
    // Clear safety timeout
    if (airStrikeSafetyTimeoutRef.current) {
      clearTimeout(airStrikeSafetyTimeoutRef.current);
      airStrikeSafetyTimeoutRef.current = null;
      console.log('⏭️ SKIP - Cleared air strike safety timeout');
    }
    
    // Stop all audio using audio manager
    stopAllAudio();
    
    // ⭐ FETCH LATEST GAME STATE: Update intelligence feed with all AI actions
    // This ensures the action log shows what happened even when animations were skipped
    console.log('🔄 Fetching latest game state to update intelligence feed...');
    fetchLatestGameState();
    
    showNotification('Your turn! (3 actions)', 'success');
  }, [showNotification, stopAllAudio, fetchLatestGameState]);
  
  // ========== RETURN ==========
  
  return {
    // Action handlers
    handleAction,
    handleEndTurn,
    skipAiTurns,  // ⭐ New function to skip AI turns
    fetchLatestGameState,  // ⭐ Refresh game state function
    
    // State
    isProcessing,
    isAirStrikeLocked,  // 🔒 Map is READ-ONLY during air strike
    lastAction,
    actionResult,
    isActionModalOpen,
    setIsActionModalOpen,
    
    // Animations
    propagandaAnimation,
    diplomacyAnimation,
    blackmailAnimation,
    airStrikeAnimation,
    deployTroopsAnimation,
    setDeployTroopsAnimation,  // ⭐ Export setter for manual triggering
    nukeButtonAnimation,  // ⭐ NEW: Nuke button press animation
    handleNukeButtonComplete,  // ⭐ NEW: Handler for button animation completion
    nuclearStrikeAnimation,
    bomberFlash,  // ⭐ New: bomber flash when airstrike available
    
    // Popups
    deployTroopsConfirmation,
    setDeployTroopsConfirmation,
    enrichmentPopup,  // ⭐ AI enrichment purchase popup
    setEnrichmentPopup,
    aaDefensePopup,  // ⭐ AA Defense purchase popup
    setAADefensePopup,
    
    // Leader states
    hiddenLeaderCode,
    sweatingLeaders,
    
    // AI processing
    isProcessingAiTurns,
    aiTurnsQueue,  // ⭐ Added for GameScreen to access
    aiActionsLog,  // ⭐ AI actions log for summary modal
    setAiActionsLog, // ⭐ Allow reset at turn start
    
    // Chat bubbles
    activeChatBubbles,
    setActiveChatBubbles,  // ✅ FIXED - was missing, causing ReferenceError
    
    // Setters needed by parent
    setLastAction,
    setIsProcessing
  };
}
